import { Injectable, inject } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

/**
 * Ce qui suit le titre de l'écran dans l'onglet du navigateur.
 *
 * Exporté pour que la fiche détail compose un titre DISTINCT au même format
 * (`<nom de l'enregistrement> · Hubscore`) : la déduction d'URL ci-dessous
 * écarte l'identifiant numérique, ce qui rendrait la fiche homonyme de sa
 * liste (RGAA 8.6). Cf. `DetailComponent.majTitreDocument`.
 */
export const NOM_PRODUIT = 'Hubscore';

/** L'ancre du contenu principal, posée par le gabarit de page. */
const ANCRE_CONTENU = 'contenu-principal';

/**
 * Le titre de l'écran, le déplacement du focus et l'annonce, à chaque
 * navigation.
 *
 * ## Pourquoi ces trois choses ensemble
 *
 * Dans une application à page unique, changer d'écran ne produit **rien** pour
 * un lecteur d'écran : le document ne se recharge pas, le focus reste où il
 * était — souvent sur un lien du menu — et rien n'annonce que le contenu a
 * changé. L'utilisateur croit que son clic n'a pas abouti.
 *
 * Les trois corrections répondent au même geste de l'utilisateur et doivent
 * donc se produire au même instant : le titre change, le focus se déplace au
 * début du contenu, et le nouveau titre est annoncé.
 *
 * ## Pourquoi une `TitleStrategy` plutôt qu'un abonnement au routeur
 *
 * Angular appelle `updateTitle` APRÈS avoir activé les routes et résolu leurs
 * titres. C'est le seul endroit où le titre définitif est connu — un
 * abonnement à `NavigationEnd` devrait le recalculer lui-même, et se
 * désynchroniserait dès qu'une route déclarerait le sien.
 *
 * ## Le repli, et pourquoi il n'est pas un aveu
 *
 * Toutes les routes ne déclarent pas encore de `title`. Plutôt que d'attendre
 * qu'elles le fassent toutes pour livrer quoi que ce soit, le titre se déduit
 * du dernier segment d'URL porteur de sens, traduit s'il correspond à une clé.
 * Une route qui déclare son titre le garde ; les autres cessent d'être
 * homonymes. RGAA 8.6 demande des titres DISTINCTS et pertinents, pas des
 * titres rédigés à la main.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly translate = inject(TranslateService);

  /**
   * Le tout premier appel correspond au CHARGEMENT du document, pas à une
   * navigation dans l'application. Voir `annonceEtDeplaceLeFocus`.
   */
  private premierAppel = true;

  override updateTitle(state: RouterStateSnapshot): void {
    const titre = this.buildTitle(state) ?? this.deduire(state.root);

    this.title.setTitle(`${titre} · ${NOM_PRODUIT}`);

    if (this.premierAppel) {
      this.premierAppel = false;

      return;
    }

    this.annonceEtDeplaceLeFocus(titre);
  }

  /**
   * Le focus part au début du contenu, pas sur le premier lien : un lecteur
   * d'écran lit alors le titre de l'écran, et la tabulation suivante entre dans
   * le contenu plutôt que de repartir du menu.
   *
   * ## Pas au chargement du document
   *
   * Ce déplacement répond à un défaut PROPRE aux applications à page unique :
   * changer d'écran n'y produit rien, parce que le document ne se recharge
   * pas. Au chargement, le navigateur fait déjà ce qu'il faut — le focus est
   * en tête de document et le lecteur d'écran lit la page.
   *
   * Le faire quand même avait deux conséquences, toutes deux relevées en
   * recette :
   *
   *  - l'anneau de focus du navigateur s'affichait autour du `<main>` entier.
   *    Recharger au clavier (F5, Ctrl+R) met Chrome en modalité clavier, et il
   *    applique alors `:focus-visible` au focus posé juste après ;
   *  - surtout, le focus se trouvait DÉJÀ dans le contenu, donc la première
   *    tabulation partait de là et sautait le lien d'évitement — qui n'avait
   *    plus aucun moyen d'être atteint.
   *
   * `setTimeout` sans délai : le contenu de la nouvelle route n'est pas encore
   * dans le document au moment où `updateTitle` s'exécute.
   */
  private annonceEtDeplaceLeFocus(titre: string): void {
    setTimeout(() => {
      const contenu = document.getElementById(ANCRE_CONTENU);
      contenu?.focus();
      void this.announcer.announce(titre, 'polite');
    });
  }

  /** Le dernier segment porteur de sens, traduit s'il correspond à une clé. */
  private deduire(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];

    for (
      let noeud: ActivatedRouteSnapshot | null = route;
      noeud;
      noeud = noeud.firstChild
    ) {
      for (const segment of noeud.url) {
        segments.push(segment.path);
      }
    }

    // Un identifiant numérique ne nomme pas un écran : on remonte au segment
    // précédent, qui porte la table ou la section.
    const parlants = segments.filter((s) => s !== '' && !/^\d+$/.test(s));
    const dernier = parlants.at(-1);

    if (dernier === undefined) {
      return NOM_PRODUIT;
    }

    const cle = `PAGE_TITLE.${dernier.toUpperCase().replace(/-/g, '_')}`;
    const traduit = this.translate.instant(cle);

    return traduit === cle ? this.humanise(dernier) : traduit;
  }

  private humanise(segment: string): string {
    const mots = segment.replace(/-/g, ' ');

    return mots.charAt(0).toUpperCase() + mots.slice(1);
  }
}
